import axios from 'axios'
import Amplify from 'aws-amplify'
import env from './env'
import Vue from 'vue'

const frurl = {
  dev: 'https://dev-api.rematch.fr',
  preprod: 'https://preprod-api.rematch.fr',
  prod: 'https://api.rematch.fr'
}

const usurl = {
  dev: 'https://dev-api-us.rematch.fr',
  preprod: 'https://preprod-api-us.rematch.fr',
  prod: 'https://api-us.rematch.fr'
}

const auth = {
  headers: {
    'Authorization': null
  }
}

const cancelToken = axios.CancelToken

let refreshInterval = null

let instance = {
  FR: axios.create({
    baseURL: frurl[env]
  }),
  US: axios.create({
    baseURL: usurl[env]
  })
}

const cancelSources = {}

function oneAtATime (method, route, auth) {
  if (cancelSources[route]) {
    return new Promise((resolve, reject) => {})
  }

  cancelSources[route] = cancelToken.source()
  return new Promise((resolve, reject) => {
    instance[Vue.prototype.$country][method](route, {
      ...auth,
      cancelToken: cancelSources[route].token
    })
      .then(data => {
        delete cancelSources[route]
        resolve(data)
      })
      .catch(err => {
        delete cancelSources[route]
        reject(err)
      })
  })
}

function replaceWithPayload (method, route, payload, auth) {
  if (cancelSources[route]) {
    cancelSources[route].cancel()
  }
  cancelSources[route] = cancelToken.source()
  const token = cancelSources[route]

  return new Promise((resolve, reject) => {
    instance[Vue.prototype.$country][method](route, payload, {
      ...auth,
      cancelToken: token.token
    })
      .then(data => {
        if (cancelSources[route].token === token) {
          delete cancelSources[route]
        }
        resolve(data)
      })
      .catch(err => {
        if (cancelSources[route].token === token) {
          delete cancelSources[route]
        }
        if (!axios.isCancel(err)) {
          reject(err)
        }
      })
  })
}

const apiWrapper = {
  //
  // Sports
  //
  createSport (sport) {
    return instance[Vue.prototype.$country].post('/sports', sport, auth)
  },

  fetchSports () {
    return oneAtATime('get', '/sports')
  },

  getSport (id) {
    return oneAtATime('get', `/sports/${id}`)
  },

  patchSport (id, patch) {
    return instance[Vue.prototype.$country].patch(`/sports/${id}`, patch, auth)
  },

  deleteSport (id) {
    return instance[Vue.prototype.$country].delete(`/sports/${id}`, auth)
  },

  //
  // Federations
  //
  createFederation (sportID, federation) {
    return instance[Vue.prototype.$country].post(`/sports/${sportID}`, federation, auth)
  },

  getSportFederations (sportID) {
    return oneAtATime('get', `/sports/${sportID}/federations`)
  },

  getFederation (id) {
    return oneAtATime('get', `/federations/${id}`)
  },

  patchFederation (id, patch) {
    return instance[Vue.prototype.$country].patch(`/federations/${id}`, patch, auth)
  },

  deleteFederation (id) {
    return instance[Vue.prototype.$country].delete(`/federations/${id}`, auth)
  },

  //
  // Categories
  //
  createCategory (federationID, category) {
    return instance[Vue.prototype.$country].post(`/federations/${federationID}/categories`, category, auth)
  },

  getFederationCategories (id) {
    return instance[Vue.prototype.$country].get(`/federations/${id}/categories`, auth)
  },

  getCategory (id) {
    return oneAtATime('get', `/categories/${id}`)
  },

  patchCategory (id, patch) {
    return instance[Vue.prototype.$country].patch(`/categories/${id}`, patch, auth)
  },

  deleteCategory (id) {
    return instance[Vue.prototype.$country].delete(`/categories/${id}`, auth)
  },

  //
  // Regions
  //
  createSubregion (id, region) {
    return instance[Vue.prototype.$country].post(`/regions/${id}`, region, auth)
  },

  getFederationRegions (id) {
    return oneAtATime('get', `/federations/${id}/regions`)
  },

  getRegion (id) {
    return oneAtATime('get', `/regions/${id}`)
  },

  deleteRegion (id) {
    return instance[Vue.prototype.$country].delete(`/regions/${id}`, auth)
  },

  //
  // Clubs
  //

  createClub (club) {
    return instance[Vue.prototype.$country].post('/clubs', club, auth)
  },

  fetchClubs (params) {
    return instance[Vue.prototype.$country].post(`/clubs/search`, params)
  },

  searchClubs (params) {
    return replaceWithPayload('post', '/clubs/search', params)
  },

  getClub (id) {
    return instance[Vue.prototype.$country].get(`/clubs/${id}`)
  },

  patchClub (id, patch) {
    return instance[Vue.prototype.$country].patch(`/clubs/${id}`, patch, auth)
  },

  deleteClub (id) {
    return instance[Vue.prototype.$country].delete(`/clubs/${id}?remove=true`, auth)
  },

  confirmDeleteClub (id) {
    return instance[Vue.prototype.$country].delete(`/clubs/${id}?remove=true&confirm=true`, auth)
  },

  uploadClubLogo (params) {
    return instance[Vue.prototype.$country].post('/clubs/upload', params, auth)
  },

  //
  // Teams
  //

  createTeam (team) {
    return instance[Vue.prototype.$country].post('/teams', team, auth)
  },

  fetchTeams (params) {
    return instance[Vue.prototype.$country].post(`/teams/search`, params)
  },

  searchTeams (params) {
    return replaceWithPayload('post', '/teams/search', params)
  },

  getTeam (id) {
    return instance[Vue.prototype.$country].get(`/teams/${id}`)
  },

  patchTeam (id, patch) {
    return instance[Vue.prototype.$country].patch(`/teams/${id}`, patch, auth)
  },

  deleteTeam (id) {
    return instance[Vue.prototype.$country].delete(`/teams/${id}?remove=true`, auth)
  },

  confirmDeleteTeam (id) {
    return instance[Vue.prototype.$country].delete(`/teams/${id}?remove=true&confirm=true`, auth)
  },

  //
  // Places
  //
  createPlace (place) {
    return instance[Vue.prototype.$country].post('/places', place, auth)
  },

  fetchPlaces (params) {
    return instance[Vue.prototype.$country].post('/places/search', params)
  },

  searchPlaces (params) {
    return replaceWithPayload('post', '/places/search', params)
  },

  getPlace (id) {
    return oneAtATime('get', `/places/${id}`)
  },

  patchPlace (id, patch) {
    return instance[Vue.prototype.$country].patch(`/places/${id}`, patch, auth)
  },

  deletePlace (id) {
    return instance[Vue.prototype.$country].delete(`/places/${id}`, auth)
  },

  //
  // Competitions
  //
  createCompetition (competition) {
    return instance[Vue.prototype.$country].post('/competitions', competition, auth)
  },

  fetchCompetitions (params) {
    return instance[Vue.prototype.$country].post('/competitions/search', params)
  },

  searchCompetitions (params) {
    return replaceWithPayload('post', '/competitions/search', params)
  },

  getCompetition (id) {
    return oneAtATime('get', `/competitions/${id}`)
  },

  patchCompetition (id, patch) {
    return instance[Vue.prototype.$country].patch(`/competitions/${id}`, patch, auth)
  },

  deleteCompetition (id) {
    return instance[Vue.prototype.$country].delete(`/competitions/${id}`, auth)
  },

  uploadCompetitionLogo (params) {
    return instance[Vue.prototype.$country].post('/competitions/upload', params, auth)
  },

  //
  // Matches
  //
  createMatch (match) {
    return instance[Vue.prototype.$country].post('/matches', match, auth)
  },

  fetchMatches (params) {
    return instance[Vue.prototype.$country].post('/matches/search', params)
  },

  searchMatches (params) {
    return replaceWithPayload('post', `/matches/search`, params)
  },

  getMatch (id) {
    return oneAtATime('get', `/matches/${id}`)
  },

  patchMatch (id, patch) {
    return instance[Vue.prototype.$country].patch(`/matches/${id}`, patch, auth)
  },

  deleteMatch (id) {
    return instance[Vue.prototype.$country].delete(`/matches/${id}?remove=true`, auth)
  },

  confirmDeleteMatch (id) {
    return instance[Vue.prototype.$country].delete(`/matches/${id}?remove=true&confirm=true`, auth)
  },

  privatizeMatchVideos ({id, ...params}) {
    return instance[Vue.prototype.$country].post(`/matches/${id}/privatize`, params || {}, auth)
  },

  //
  // Videos
  //
  fetchVideos (params) {
    return instance[Vue.prototype.$country].post('/videos/search', params)
  },

  searchVideos (params) {
    return replaceWithPayload('post', '/videos/search', params, auth)
  },

  getVideo (id) {
    return oneAtATime('get', `/videos/${id}`, auth)
  },

  patchVideo (id, patch) {
    return instance[Vue.prototype.$country].patch(`/videos/${id}`, patch, auth)
  },

  deleteVideo (id) {
    return instance[Vue.prototype.$country].delete(`/videos/${id}`, auth)
  },

  hitVideo (id) {
    return instance[Vue.prototype.$country].post(`/videos/${id}/hit`)
  },

  suspendVideo (id) {
    return instance[Vue.prototype.$country].post(`/videos/${id}/police`, {reason: 'Signalée depuis le back office'}, auth)
  },

  suspendMatchVideos (id) {
    return instance[Vue.prototype.$country].put(`/videos/police-match/${id}`, {reason: 'Signalée depuis le back office'}, auth)
  },

  unsuspendVideo (id) {
    return instance[Vue.prototype.$country].post(`/videos/${id}/unsuspend`, {}, auth)
  },

  unsuspendMatchVideos (matchID) {
    return instance[Vue.prototype.$country].post(`/matches/${matchID}/unsuspend-videos`, {}, auth)
  },

  //
  // Users
  //
  fetchUsers (params) {
    return instance[Vue.prototype.$country].post('/users/search', params, auth)
  },

  searchUsers (params) {
    return replaceWithPayload('post', '/users/search', params, auth)
  },

  getUser (id) {
    let ts = Date.now()
    return oneAtATime('get', `/users/${id}/details?preventCache=${ts}`, auth)
  },

  // patchUser (id, patch) {
  //   return instance[Vue.prototype.$country].patch(`/users/${id}`, patch, auth)
  // },

  deleteUser (params) {
    return instance[Vue.prototype.$country].delete(
      `/users/${params.id}`,
      {
        data: JSON.stringify({confirm: params.confirm}),
        ...auth
      }
    )
  },

  setUserPremium (id) {
    return instance[Vue.prototype.$country].post(`/users/${id}/premium`, {}, auth)
  },

  unsetUserPremium (id) {
    return instance[Vue.prototype.$country].delete(`/users/${id}/premium`, auth)
  },

  notifyUser (id, notification) {
    return instance[Vue.prototype.$country].post(`/users/${id}/notification`, notification, auth)
  },

  notifyGroup (topic, notification) {
    // return Promise.resolve({})
    return instance[Vue.prototype.$country].post(`/users/groupnotification/${topic}`, notification, auth)
  },

  //
  // Notifications
  //
  createNotification (notif) {
    return instance[Vue.prototype.$country].post('/notifications', notif, auth)
  },

  sendNotification (notif) {
    return instance[Vue.prototype.$country].put(`/notifications/${notif.label}/send`, notif.params, auth)
  },

  fetchNotifications () {
    return oneAtATime('get', '/notifications?is_auto=true', auth)
  },

  fetchAllNotifications () {
    return oneAtATime('get', '/notifications', auth)
  },

  getNotification (id) {
    return oneAtATime('get', `/notifications/${id}`, auth)
  },

  patchNotification (id, patch) {
    return instance[Vue.prototype.$country].patch(`/notifications/${id}`, patch, auth)
  },

  //
  // Top Actions
  //
  createTopAction (top) {
    return instance[Vue.prototype.$country].post('/topactions', top, auth)
  },

  fetchTopActions (params) {
    return instance[Vue.prototype.$country].post('/topactions/search', params, auth)
  },

  searchTopActions (params) {
    return replaceWithPayload('post', `/topactions/search`, params, auth)
  },

  getTopAction (id) {
    return oneAtATime('get', `/topactions/${id}`, auth)
  },

  deleteTopAction (id) {
    return instance[Vue.prototype.$country].delete(`/topactions/${id}`, auth)
  },

  //
  // Clips
  //

  getClip (id) {
    return oneAtATime('get', `/clips/${id}`, auth)
  },

  searchClips (params) {
    return replaceWithPayload('post', `/clips/search`, params, auth)
  },

  patchClip (id, patch) {
    return instance[Vue.prototype.$country].patch(`/clips/${id}`, patch, auth)
  },

  deleteClip (id) {
    return instance[Vue.prototype.$country].delete(`/clips/${id}`, auth)
  },

  //
  // Connexion
  //
  login (username, password) {
    return Amplify.Auth.signIn(username, password)
  },

  disconnect () {
    return Amplify.Auth.signOut()
      .then(user => {
        if (refreshInterval !== null) {
          window.clearInterval(refreshInterval)
        }
        return user
      })
  },

  setConnected (token = null) {
    if (token) {
      auth.headers.Authorization = token
    }

    if (refreshInterval !== null) {
      window.clearInterval(refreshInterval)
    }
    refreshInterval = window.setInterval(refreshToken, 300000)
  }
}

function refreshToken () {
  Amplify.Auth.currentSession()
    .then(session => {
      // User is logged in. Set auth header on all requests
      auth.headers.Authorization = session.idToken.jwtToken
    })
    .catch(() => {
      console.error('Failed to refresh user token')
    })
}

export default apiWrapper



// WEBPACK FOOTER //
// ./src/tools/api.js